import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, AlertOctagon, Hourglass, Loader2, ArrowLeft, Send, RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { lawyerApi } from "@lawyer/services/lawyerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/**
 * Bar Council verification — dedicated status + submit/resubmit page.
 *
 * Single destination for the widget's "View verification status" and
 * "Resubmit verification" CTAs. Shows every field the spec asks for and
 * keeps the form idempotent (same POST endpoint handles first submit
 * and re-submit; server clears the previous rejection reason).
 */
const STATE_BAR_COUNCILS = [
  "Andhra Pradesh", "Assam & other North-East States", "Bihar", "Chhattisgarh",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra & Goa", "Manipur",
  "Meghalaya", "Odisha", "Punjab & Haryana", "Rajasthan", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const STATUS_META = {
  not_started:   { icon: ShieldCheck, tone: "slate",   label: "Not started" },
  submitted:     { icon: Hourglass,   tone: "amber",   label: "Submitted" },
  under_review:  { icon: Hourglass,   tone: "amber",   label: "Verification in progress" },
  verified:      { icon: ShieldCheck, tone: "emerald", label: "Verified Advocate" },
  rejected:      { icon: AlertOctagon,tone: "rose",    label: "Verification rejected" },
};

const TONE_CLS = {
  slate:   { pill: "bg-slate-100 text-slate-700",   ring: "ring-slate-200", card: "bg-white" },
  amber:   { pill: "bg-amber-100 text-amber-800",   ring: "ring-amber-200", card: "bg-amber-50/40" },
  emerald: { pill: "bg-emerald-100 text-emerald-800", ring: "ring-emerald-200", card: "bg-emerald-50/40" },
  rose:    { pill: "bg-rose-600 text-white",        ring: "ring-rose-200",  card: "bg-rose-50/40" },
};

export default function LawyerBarCouncilPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ enrolment_number: "", state_bar_council: "", name_on_record: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await lawyerApi.get("/lawyer/bar-council/status").then((r) => r.data);
      setStatus(s);
      setForm({
        enrolment_number: s.enrolment_number || "",
        state_bar_council: s.state_bar_council || "",
        name_on_record: s.name_on_record || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load status");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.enrolment_number.trim()) {
      toast.error("Enrolment number is required");
      return;
    }
    setBusy(true);
    try {
      await lawyerApi.post("/lawyer/bar-council/submit", form);
      toast.success("Submitted for verification");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !status) {
    return (
      <div className="min-h-[400px] flex items-center justify-center" data-testid="bar-council-loading">
        <Loader2 size={20} className="animate-spin text-slate-300" />
      </div>
    );
  }

  const meta = STATUS_META[status.status] || STATUS_META.not_started;
  const tone = TONE_CLS[meta.tone];
  const Icon = meta.icon;

  const readOnly = status.status === "verified"
    || status.status === "submitted" || status.status === "under_review";

  return (
    <div data-testid="bar-council-page">
      <Link
        to="/lawyer-portal"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0A2540]"
        data-testid="bar-council-back"
      >
        <ArrowLeft size={12} /> Back to dashboard
      </Link>
      <h1
        className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A2540]"
        style={{ fontFamily: "Poppins" }}
      >
        Bar Council verification
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        A core trust signal that unlocks payouts, availability, and the Verified Advocate badge on your public profile.
      </p>

      {/* Status card */}
      <div
        className={`mt-6 rounded-2xl border border-slate-200 ring-1 ${tone.ring} ${tone.card} p-5`}
        data-testid="bar-council-status-card"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 grid place-items-center">
            <Icon size={20} className={
              meta.tone === "emerald" ? "text-emerald-600"
              : meta.tone === "amber" ? "text-amber-600 animate-pulse"
              : meta.tone === "rose"  ? "text-rose-600" : "text-slate-500"
            } />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${tone.pill}`}
                data-testid="bar-council-status-pill"
              >
                {meta.label}
              </span>
              {status.verified_at && (
                <span className="text-xs text-slate-500">
                  Verified on {new Date(status.verified_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {status.informational_message && (
              <p className="mt-2 text-sm text-slate-700">{status.informational_message}</p>
            )}
            {status.rejection_reason && (
              <p className="mt-2 text-sm text-rose-800" data-testid="bar-council-rejection-reason">
                <strong>Why it was rejected:</strong> {status.rejection_reason}
              </p>
            )}
            {status.reviewer_notes && (
              <p className="mt-2 text-sm text-slate-700" data-testid="bar-council-reviewer-notes">
                <strong>Reviewer notes:</strong> {status.reviewer_notes}
              </p>
            )}
          </div>
        </div>

        {/* Details grid */}
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field k="Bar Council enrolment number" v={status.enrolment_number} />
          <Field k="State Bar Council" v={status.state_bar_council} />
          <Field k="Name on record" v={status.name_on_record} />
          <Field k="Submitted on" v={status.submitted_at ? new Date(status.submitted_at).toLocaleString() : null} />
          <Field k="Reviewed on" v={status.reviewed_at ? new Date(status.reviewed_at).toLocaleString() : null} />
          <Field k="Verified on" v={status.verified_at ? new Date(status.verified_at).toLocaleString() : null} />
        </dl>
      </div>

      {/* Form — hidden when read-only */}
      {!readOnly && (
        <form
          onSubmit={onSubmit}
          className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
          data-testid="bar-council-form"
        >
          <div>
            <h2 className="font-bold text-[#0A2540]">
              {status.status === "rejected" ? "Resubmit your details" : "Submit your Bar Council details"}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              We verify against your State Bar Council directory. This usually takes 1–2 business days.
            </p>
          </div>

          <label className="block">
            <span className="block text-xs font-bold text-[#0A2540] mb-1">
              Enrolment number *
            </span>
            <Input
              value={form.enrolment_number}
              onChange={(e) => setForm({ ...form, enrolment_number: e.target.value.toUpperCase() })}
              placeholder="e.g. MAH/12345/2018"
              data-testid="bar-council-enrolment-input"
              required
            />
          </label>

          <label className="block">
            <span className="block text-xs font-bold text-[#0A2540] mb-1">State Bar Council</span>
            <select
              value={form.state_bar_council}
              onChange={(e) => setForm({ ...form, state_bar_council: e.target.value })}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              data-testid="bar-council-state-input"
            >
              <option value="">Select state bar council…</option>
              {STATE_BAR_COUNCILS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-bold text-[#0A2540] mb-1">Name on Bar Council record</span>
            <Input
              value={form.name_on_record}
              onChange={(e) => setForm({ ...form, name_on_record: e.target.value })}
              placeholder="Match exactly as printed on your enrolment certificate"
              data-testid="bar-council-name-input"
            />
          </label>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-xl adv-btn-primary font-extrabold"
            data-testid="bar-council-submit-btn"
          >
            {busy
              ? <Loader2 size={16} className="animate-spin mr-1" />
              : status.status === "rejected"
                ? <RefreshCw size={16} className="mr-1" />
                : <Send size={16} className="mr-1" />}
            {status.status === "rejected" ? "Resubmit for verification" : "Submit for verification"}
          </Button>
        </form>
      )}
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{k}</dt>
      <dd className="text-[#0A2540] font-medium mt-0.5">{v || <span className="text-slate-300">—</span>}</dd>
    </div>
  );
}
